import React, { useState, useMemo } from 'react';
import { useMutation } from 'react-query';
import { API, APIRoutes } from '@utils/api';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSidePropsContext } from 'next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation, Trans } from 'next-i18next';
import Head from 'next/head';
import Lottie from 'react-lottie';
import { ErrorMessage } from '@styles/globalStyles';

import { AuthForm } from '@components/Form/styles';
import { Checkboxes, CandidateInfo, Switcher, FieldsWrapper, SuccessWrapper } from '@pages/signup/style';
import Button, { BUTTON_TYPE } from '@components/Button';
import Link from '@components/Link';
import TextInput, { INPUT_TYPES as type } from '@components/Form/Input';
import PasswordInput from '@components/Form/Input/PasswordInput';
import Control, { CONTROL_TYPE } from '@components/Form/Control';
import EducationSection from '@components/Profile/Education';
import LanguageSection from '@components/Profile/Language';
import { apiValidationHandler } from '@utils/formHelpers';
import Preloader, { SIZES } from '@components/Preloader';
import routes from '@utils/routes';
import MailSentAnimation from '@assets/lottie/mail-sent.json';
import { hasAllNullValues } from '@utils/dataHelpers';

function Signup() {
  const { t } = useTranslation(['register', 'common']);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        accountType: Yup.string().default('student').required('Required'),
        email: Yup.string().email(t('Email invalid')).required(t('Email Required')),
        password: Yup.string().required(t('Password Required')),
        passwordConfirmation: Yup.string()
          .required(t('Password Required'))
          .oneOf([Yup.ref('password'), null], t('Passwords must match')),
        firstName: Yup.string().required(t('Name Required')),
        lastName: Yup.string().required(t('Surname Required')),
        termsAndConditions: Yup.boolean()
          .required(t('PersonalData Required'))
          .oneOf([true], t('PersonalData Required')),
        rodo: Yup.boolean().required(t('RODO Required')).oneOf([true], t('RODO Required')),
        studentProfileAttributes: Yup.lazy(() => {
          // if (parent.accountType === 'student') {
          //   return Yup.object().shape({
          //     studentProfileLanguagesAttributes: Yup.array().of(
          //       Yup.object().shape({
          //         languageId: Yup.number().typeError(t('Choose Language')).min(1, t('Choose Language')),
          //         level: Yup.string().min(1, t('Choose Level')),
          //       })
          //     ),
          //     studentHighschoolProfilesAttributes: Yup.array().of(
          //       Yup.object().shape({
          //         highschoolId: Yup.number().typeError(t('Choose University')).min(1, t('Choose University')),
          //         year: Yup.string().min(2, t('Choose a Year of Graduation')),
          //         highschoolProfileId: Yup.number()
          //           .typeError(t('Choose Field of Study'))
          //           .min(1, t('Choose Field of Study')),
          //       })
          //     ),
          //   });
          // }
          return Yup.mixed().notRequired();
        }),
      }),
    [t]
  );

  const formOptions = { resolver: yupResolver(validationSchema) };
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setError,
  } = useForm(formOptions);

  const { mutate: registerUser, isLoading } = useMutation((data: SignupPayload) =>
    API.post(APIRoutes.auth.signup, { user: data })
  );

  const onSubmit = (user: SignupPayload) => {
    // console.log('payload:', user);
    if (user.accountType === 'employer') {
      // eslint-disable-next-line no-param-reassign
      delete user.studentProfileAttributes;
    }
    if (user.accountType === 'student') {
      const langs = (user.studentProfileAttributes || {}).studentProfileLanguagesAttributes.filter(
        (lang) => !hasAllNullValues(lang)
      );
      const unis = (user.studentProfileAttributes || {}).studentHighschoolProfilesAttributes.filter(
        (uni) => !hasAllNullValues(uni)
      );
      // eslint-disable-next-line no-param-reassign
      user.studentProfileAttributes.studentProfileLanguagesAttributes = langs;
      // eslint-disable-next-line no-param-reassign
      user.studentProfileAttributes.studentHighschoolProfilesAttributes = unis;
    }
    registerUser(user, {
      onError: ({ response: { data } }) => {
        apiValidationHandler(data.errors, setError, user);
      },
      onSuccess: () => {
        setShowConfirmation(true);
      },
    });
  };

  const typeForm = watch('accountType', 'student');

  if (showConfirmation) {
    const animationOptions = {
      loop: true,
      autoplay: true,
      animationData: MailSentAnimation,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <SuccessWrapper>
        <Lottie options={animationOptions} height={250} width={250} />
        <h2>{t('Your account has been successfully created!')}</h2>
        <h3>{t('Check your email to activate yout account')}</h3>
      </SuccessWrapper>
    );
  }

  return (
    <AuthForm onSubmit={handleSubmit(onSubmit)}>
      <Head>
        <title>{t('Registration')}</title>
        <meta property="og:title" content="Sign up" />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}/images/lodz.png`} />
        <meta property="og:image:secure_url" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}/images/lodz.png`} />
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="279" />
        <meta property="og:description" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_PORTAL_URL}/rejestracja`} />
      </Head>
      <FieldsWrapper>
        {isLoading ? <Preloader size={SIZES.LARGE} overlay /> : null}
        <Switcher>
          <Controller
            control={control}
            name="accountType"
            defaultValue="student"
            render={({ field: { name, onChange, value } }) => (
              <>
                <Control
                  name={name}
                  onChange={onChange}
                  value="student"
                  type={CONTROL_TYPE.RADIO}
                  label={t('Candidate')}
                  checked={value === 'student'}
                  centered
                />
                <Control
                  name={name}
                  onChange={onChange}
                  value="employer"
                  type={CONTROL_TYPE.RADIO}
                  label={t('Employer')}
                  checked={value === 'employer'}
                  centered
                />
              </>
            )}
          />
        </Switcher>

        <Controller
          name="email"
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <TextInput
              name={name}
              inputType={type.TEXT}
              onChange={onChange}
              value={value || ''}
              label={t('Email')}
              error={errors[name]}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <PasswordInput
              name={name}
              autoComplete="off"
              onChange={onChange}
              value={value || ''}
              label={t('Password')}
              error={errors[name]}
              showStrength
            />
          )}
        />

        <Controller
          name="passwordConfirmation"
          defaultValue=""
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <PasswordInput
              name={name}
              autoComplete="off"
              onChange={onChange}
              value={value || ''}
              label={t('Repeat Password')}
              error={errors[name]}
            />
          )}
        />

        <Controller
          name="firstName"
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <TextInput
              name={name}
              inputType={type.TEXT}
              onChange={onChange}
              value={value || ''}
              label={t('Name')}
              error={errors[name]}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <TextInput
              name={name}
              inputType={type.TEXT}
              onChange={onChange}
              value={value || ''}
              label={t('Surname')}
              error={errors[name]}
            />
          )}
        />

        {typeForm === 'student' && (
          <>
            <CandidateInfo dangerouslySetInnerHTML={{ __html: t('Candidate information') }} />
            <LanguageSection
              fieldNamePrefix="studentProfileAttributes"
              errors={errors}
              control={control}
              watch={watch}
            />

            <EducationSection
              fieldNamePrefix="studentProfileAttributes"
              control={control}
              watch={watch}
              errors={errors}
            />
          </>
        )}

        <Checkboxes>
          <Controller
            control={control}
            name="termsAndConditions"
            render={({ field: { name, onChange, value } }) => (
              <Control
                type={CONTROL_TYPE.CHECKBOX}
                onChange={onChange}
                value={value}
                name={name}
                checked={value}
                error={errors[name]}
                label={
                  typeForm === 'student' ? (
                    <Trans i18nKey="personal_data_protection_candidate" t={t}>
                      Zgodnie z art. 6 ust. 1 lit a ogólnego rozporządzenia o ochronie danych osobowych z dnia 27
                      kwietnia 2016 r. <Link href="/#">więcej</Link>
                    </Trans>
                  ) : (
                    <Trans i18nKey="personal_data_protection" t={t}>
                      Zgodnie z art. 6 ust. 1 lit a ogólnego rozporządzenia o ochronie danych osobowych z dnia 27
                      kwietnia 2016 r. <Link href="/#">więcej</Link>
                    </Trans>
                  )
                }
              />
            )}
          />

          <Controller
            control={control}
            name="rodo"
            render={({ field: { name, onChange, value } }) => (
              <Control
                onChange={onChange}
                value={value}
                name={name}
                error={errors[name]}
                checked={value}
                label={
                  <Trans i18nKey="terms_and_cond" t={t}>
                    Oświadczam, że zapoznałem się i akceptuję <Link href={t('rodo')}>Klauzulę Informacyjną RODO</Link>{' '}
                    oraz <Link href={t('terms')}>Regulamin Serwisu</Link>.
                  </Trans>
                }
                type={CONTROL_TYPE.CHECKBOX}
              />
            )}
          />
        </Checkboxes>
      </FieldsWrapper>
      {errors && Object.keys(errors).length > 0 && (
        <ErrorMessage style={{ marginBottom: 10, textAlign: 'left' }}>{t('common:Errors')}</ErrorMessage>
      )}
      <Button type={BUTTON_TYPE.SUBMIT} isLoading={isLoading}>
        {t('Sign up')}
      </Button>
      <span>
        {t('Have an account?')} &nbsp;
        <Link href={routes.signin} replace>
          {t('Sign in')}
        </Link>
      </span>
    </AuthForm>
  );
}

export async function getServerSideProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'register', 'languageLevels'])),
    },
  };
}

export default Signup;
